import ExcelJS from "exceljs";
import api, {
  attendanceService,
  attendanceStructureService,
  employeeService,
} from "../../../services/api";
import {
  isHalfDayIoFormatConfigured,
  parseHalfDayIoLenient,
  parseHalfDayIoStrict,
} from "../../../utils/attendanceHalfDayIo";

function triggerBlobDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

function blobFromAxios(res) {
  return res?.data instanceof Blob ? res.data : new Blob([res.data]);
}

/** Prefer server JSON message when a blob download failed with an error body. */
export async function messageFromBlobDownloadError(err) {
  if (err?.code === "ECONNABORTED" || /timeout/i.test(String(err?.message || ""))) {
    return "Template download timed out. Please try again.";
  }
  if (!err?.response) {
    const host =
      typeof window !== "undefined" ? String(window.location.hostname || "") : "";
    const isLocal =
      !host ||
      host === "localhost" ||
      host === "127.0.0.1" ||
      host.endsWith(".local");
    if (isLocal) {
      return "Network Error — cannot reach the server. Ensure the backend is running (port 3001), then retry.";
    }
    return "Network Error — cannot reach the API. Check your connection, or ask an admin if the API/CORS proxy is up, then retry.";
  }
  const data = err.response.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text);
      if (parsed?.message) return String(parsed.message);
    } catch {
      /* ignore */
    }
  }
  if (err.response.data?.message) return String(err.response.data.message);
  return err?.message || "Failed to download template";
}

function toAttDateIso(year, monthIndex0, day) {
  const y = Number(year);
  const m = Number(monthIndex0);
  const d = Number(day);
  if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) return null;
  if (m < 0 || m > 11 || d < 1 || d > 31) return null;
  const dt = new Date(y, m, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== m || dt.getDate() !== d) return null;
  return `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

function parseEmpDateIso(value) {
  if (value == null || value === "") return null;
  const s = String(value).trim();
  if (!s) return null;
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const dmy = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
  if (dmy) {
    return `${dmy[3]}-${String(dmy[2]).padStart(2, "0")}-${String(dmy[1]).padStart(2, "0")}`;
  }
  const dt = new Date(s);
  if (Number.isNaN(dt.getTime())) return null;
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
}

function ymdNum(iso) {
  if (!iso) return null;
  const n = parseInt(String(iso).replace(/-/g, ""), 10);
  return Number.isFinite(n) ? n : null;
}

const EMP_TEMPLATE_LABELS = {
  org: "Org_Details",
  personal: "Personal_Details",
  family: "Family_Details",
  bank: "Bank_Details",
  qualification: "Education_Details",
  professional: "Profession_Details",
  statutory: "Statutory_Details",
};

const EMP_EXPORT_FN = {
  org: employeeService.exportOrgDetails,
  personal: employeeService.exportPersonalDetails,
  family: employeeService.exportFamilyDetails,
  bank: employeeService.exportBankDetails,
  qualification: employeeService.exportQualificationDetails,
  professional: employeeService.exportProfessionalDetails,
  statutory: employeeService.exportStatutoryDetails,
};

export const WORKSPACE_IO_DEFS = {
  "emp-import": {
    title: "Import Employee Details",
    description: "Choose a section, download its template, then validate before import.",
    hasTemplate: true,
    hasSection: true,
    needsFile: true,
    primaryLabel: "Validate",
    successToast: "Import completed",
    closeOnSuccess: false,
  },
  "emp-export": {
    title: "Export Employee Data",
    description: "Download employee data for a section or the full export.",
    hasTemplate: false,
    hasSection: true,
    needsFile: false,
    primaryLabel: "Export",
    successToast: "Export downloaded",
  },
  "emp-bulk-exit": {
    title: "Bulk Employees Exit",
    description:
      "Download the template, fill Relieved/Terminated rows, validate, then confirm. Updates employee profile status and dates.",
    hasTemplate: true,
    hasSection: false,
    needsFile: true,
    primaryLabel: "Validate",
    successToast: "Employee exits imported",
    closeOnSuccess: false,
  },
  "att-import": {
    title: "Bulk Import Attendance",
    description: "Download the month template, fill statuses, then validate before import.",
    hasTemplate: true,
    hasPeriod: true,
    needsFile: true,
    primaryLabel: "Validate",
    successToast: "Attendance imported",
    closeOnSuccess: false,
  },
  "att-import-leave": {
    title: "Bulk Leave Upload",
    description: "Download the leave template, fill rows, then upload.",
    hasTemplate: true,
    hasPeriod: true,
    needsFile: true,
    primaryLabel: "Upload",
    successToast: "Leave file accepted — open Attendance to finish applying if needed",
    closeOnSuccess: false,
  },
  "att-export": {
    title: "Export Attendance",
    description: "Download attendance data for the selected month.",
    hasTemplate: false,
    hasPeriod: true,
    needsFile: false,
    primaryLabel: "Export",
    successToast: "Attendance export downloaded",
  },
  "roles-export": {
    title: "Export Roles & permissions",
    description:
      "Download every saved role with its granted screen actions as Excel. Edit the file, then Import to create or update custom roles.",
    hasTemplate: false,
    hasSection: false,
    needsFile: false,
    primaryLabel: "Export",
    successToast: "Roles export downloaded",
    showRolesPreview: true,
  },
  "roles-import": {
    title: "Import Roles & permissions",
    description:
      "Upload an Excel export (or template). Custom roles are created or updated from the granted access columns. System roles are skipped. Validate first, then confirm.",
    hasTemplate: true,
    hasSection: false,
    needsFile: true,
    primaryLabel: "Validate",
    successToast: "Roles imported",
    closeOnSuccess: false,
  },
};

export async function downloadWorkspaceIoTemplate(featureId, ctx) {
  if (featureId === "emp-import") {
    const section = ctx.section || "org";
    const res = await employeeService.downloadImportTemplate(section);
    triggerBlobDownload(
      blobFromAxios(res),
      `${EMP_TEMPLATE_LABELS[section] || section}_Import_Template.xlsx`
    );
    return;
  }

  if (featureId === "emp-bulk-exit") {
    const res = await employeeService.downloadBulkExitImportTemplate();
    triggerBlobDownload(
      blobFromAxios(res),
      "Bulk_Employees_Exit_Import_Template.xlsx"
    );
    return;
  }

  if (featureId === "att-import") {
    const res = await attendanceService.downloadTemplate(ctx.month, ctx.year);
    triggerBlobDownload(
      blobFromAxios(res),
      `Attendance_Template_${ctx.year}_${String(ctx.month + 1).padStart(2, "0")}.xlsx`
    );
    return;
  }

  if (featureId === "att-import-leave") {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Leave Upload");
    ws.addRow([
      "Employee Code",
      "Leave Type",
      "From Date",
      "To Date",
      "Days",
      "Reason",
    ]);
    ws.getRow(1).font = { bold: true };
    ws.getColumn(3).numFmt = "dd/mm/yyyy";
    ws.getColumn(4).numFmt = "dd/mm/yyyy";
    ws.addRow(["MK0001", "CL", "01/07/2026", "01/07/2026", 1, "Personal"]);
    const buf = await wb.xlsx.writeBuffer();
    triggerBlobDownload(
      new Blob([buf], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      "Leave_Upload_Template.xlsx"
    );
    return;
  }

  if (featureId === "roles-import") {
    const res = await api.get("/global-settings/roles-permissions/export", {
      responseType: "blob",
    });
    triggerBlobDownload(blobFromAxios(res), "Roles_Permissions_Template.xlsx");
    return;
  }

  throw new Error("No template for this action");
}

export async function validateWorkspaceRolesImport(ctx) {
  if (!ctx.file) throw new Error("Please choose a file");
  const formData = new FormData();
  formData.append("file", ctx.file);
  const res = await api.post("/global-settings/roles-permissions/validate", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  const data = res?.data?.data || {};
  const invalidRecords = (data.invalidRecords || []).map((r) => ({
    ...r,
    status: r.message || "Skipped",
  }));
  const roles = (data.roles || []).map((r) => ({
    roleCode: r.roleCode,
    roleName: r.roleName,
    description: r.description,
    active: r.active,
    screenAccess: r.screenAccess || {},
  }));
  const validRecords = roles.map((r) => ({
    ...r,
    empCode: r.roleCode,
    employeeCode: r.roleCode,
    name: r.roleName,
    status: "OK",
  }));
  return {
    validRecords,
    invalidRecords,
    hardBlockImport: false,
    roles,
  };
}

export async function confirmWorkspaceRolesImport(roles) {
  const res = await api.post("/global-settings/roles-permissions/import", { roles });
  if (res?.data?.success === false) {
    throw new Error(res.data.message || "Import failed");
  }
  return res?.data?.message || "Roles imported";
}

function formatInvalidSummary(invalidRecords = [], limit = 5) {
  const rows = Array.isArray(invalidRecords) ? invalidRecords : [];
  if (!rows.length) return "";
  const lines = rows.slice(0, limit).map((rec) => {
    const name =
      rec?.record?.["Name*"] ||
      rec?.record?.Name ||
      rec?.record?.name ||
      "Unknown";
    const code =
      rec?.record?.["Employee Code*"] ||
      rec?.record?.["Employee Code"] ||
      "(no code)";
    const detail =
      (rec?.fieldErrors &&
        Object.entries(rec.fieldErrors)
          .map(([f, m]) => `${f}: ${m}`)
          .join("; ")) ||
      rec?.error ||
      "Validation failed";
    return `Row ${rec.row ?? "?"}: ${code} / ${name} — ${detail}`;
  });
  const more =
    rows.length > limit ? `\n…and ${rows.length - limit} more row(s).` : "";
  return `${lines.join("\n")}${more}`;
}

/** Build and download an Excel error report for failed import rows. */
export async function downloadImportErrorReport(
  invalidRecords = [],
  filename = "Import_Validation_Errors.xlsx"
) {
  const rows = Array.isArray(invalidRecords) ? invalidRecords : [];
  if (!rows.length) throw new Error("No invalid rows to export");

  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("Validation Errors");
  ws.columns = [
    { header: "Sl No", key: "sl", width: 8 },
    { header: "Excel Row", key: "row", width: 10 },
    { header: "Employee Code", key: "code", width: 16 },
    { header: "Employee Name", key: "name", width: 28 },
    { header: "Mobile", key: "mobile", width: 14 },
    { header: "Official Email", key: "email", width: 28 },
    { header: "Error Message", key: "error", width: 70 },
  ];
  ws.getRow(1).font = { bold: true };

  rows.forEach((rec, idx) => {
    const record = rec?.record || {};
    const errorMsg =
      (rec?.fieldErrors &&
        Object.entries(rec.fieldErrors)
          .map(([f, m]) => `${f}: ${m}`)
          .join("\n")) ||
      rec?.error ||
      "Validation failed";
    ws.addRow({
      sl: idx + 1,
      row: rec.row ?? "",
      code:
        record["Employee Code*"] ||
        record["Employee Code"] ||
        record.empCode ||
        "",
      name: record["Name*"] || record.Name || record.name || "",
      mobile:
        record["Mobile Number*"] ||
        record["Mobile Number"] ||
        record.mobile ||
        "",
      email:
        record["Official Email*"] ||
        record["Official Email"] ||
        record.email ||
        "",
      error: errorMsg,
    });
  });

  const buf = await wb.xlsx.writeBuffer();
  triggerBlobDownload(
    new Blob([buf], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    filename
  );
}

/**
 * Validate employee import only — does not write. Returns API payload + counts.
 */
export async function validateWorkspaceEmpImport(ctx) {
  if (!ctx?.file) throw new Error("Please choose a file");
  const section = ctx.section || "org";
  const formData = new FormData();
  formData.append("file", ctx.file);
  const response = await employeeService.validateEmployeeImport(formData, section);
  const data = response?.data || response;
  return {
    section,
    validRecords: data?.validRecords || response?.validRecords || [],
    invalidRecords: data?.invalidRecords || response?.invalidRecords || [],
    validCount: data?.validCount ?? response?.validCount ?? 0,
    invalidCount: data?.invalidCount ?? response?.invalidCount ?? 0,
    totalRecords: data?.totalRecords ?? response?.totalRecords ?? 0,
  };
}

/**
 * Confirm import of already-validated successful records (after user consent).
 */
export async function confirmWorkspaceEmpImport(section, validRecords) {
  const records = Array.isArray(validRecords) ? validRecords : [];
  if (!records.length) throw new Error("No successful rows to import");
  if (section === "org") {
    await employeeService.importConfirmedEmployees(records);
  } else {
    await employeeService.importSectionRecords(section, records);
  }
  return `Imported ${records.length} row(s).`;
}

/**
 * Validate bulk exit import only — does not write.
 */
export async function validateWorkspaceEmpBulkExit(ctx) {
  if (!ctx?.file) throw new Error("Please choose a file");
  const formData = new FormData();
  formData.append("file", ctx.file);
  const response = await employeeService.validateBulkExitImport(formData);
  const data = response?.data || response;
  return {
    section: "bulk-exit",
    validRecords: data?.validRecords || response?.validRecords || [],
    invalidRecords: data?.invalidRecords || response?.invalidRecords || [],
    validCount: data?.validCount ?? response?.validCount ?? 0,
    invalidCount: data?.invalidCount ?? response?.invalidCount ?? 0,
    totalRecords: data?.totalRecords ?? response?.totalRecords ?? 0,
  };
}

/**
 * Confirm bulk exit for already-validated rows.
 */
export async function confirmWorkspaceEmpBulkExit(validRecords) {
  const records = Array.isArray(validRecords) ? validRecords : [];
  if (!records.length) throw new Error("No successful rows to import");
  const response = await employeeService.confirmBulkExitImport(records);
  const data = response?.data || response;
  const ok = data?.successCount ?? records.length;
  const fail = data?.failCount ?? 0;
  return fail
    ? `Exited ${ok} employee(s); ${fail} failed.`
    : `Exited ${ok} employee(s). Profile status and dates updated.`;
}

const normalizeAttHeaderKey = (v) =>
  String(v || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

const excelJsCellText = (cell) => {
  if (!cell) return "";
  const text = cell.text != null ? String(cell.text).trim() : "";
  if (text) return text;
  const v = cell.value;
  if (v == null) return "";
  if (v instanceof Date && !Number.isNaN(v.getTime())) {
    return `${String(v.getDate()).padStart(2, "0")}/${String(
      v.getMonth() + 1
    ).padStart(2, "0")}/${v.getFullYear()}`;
  }
  if (typeof v === "object") {
    if (Array.isArray(v.richText)) {
      return v.richText.map((t) => t?.text || "").join("").trim();
    }
    if (v.result != null) {
      if (v.result instanceof Date && !Number.isNaN(v.result.getTime())) {
        const d = v.result;
        return `${String(d.getDate()).padStart(2, "0")}/${String(
          d.getMonth() + 1
        ).padStart(2, "0")}/${d.getFullYear()}`;
      }
      return String(v.result).trim();
    }
    if (v.text != null) return String(v.text).trim();
    return "";
  }
  return String(v).trim();
};

const CORE_ATT_IMPORT_CODES = new Set([
  "PR",
  "PH",
  "WO",
  "LOP",
  "LP",
  "LWP",
  "HD",
  "AB",
  "WOPR",
  "PRWO",
]);

/** Split concatenated half-day codes like PRLOP into two known codes. */
function splitCombinedHalfDayCode(rawStatus, knownCodes = new Set()) {
  const s = String(rawStatus || "")
    .trim()
    .toUpperCase();
  if (!s || s.length < 4) return null;
  if (s.includes("/") || s.includes("+")) return null;

  const fallbackKnown = new Set([
    "PR",
    "PH",
    "WO",
    "LOP",
    "LP",
    "LWP",
    "HD",
    "AB",
    "WOPR",
    "PRWO",
  ]);
  const isKnown = (code) => fallbackKnown.has(code) || knownCodes.has(code);

  let best = null;
  for (let i = 1; i < s.length; i += 1) {
    const left = s.slice(0, i);
    const right = s.slice(i);
    if (!left || !right) continue;
    let score = 0;
    if (isKnown(left)) score += 4;
    if (isKnown(right)) score += 4;
    if (left.length === 3) score += 1;
    if (right.length === 3) score += 1;
    if (left.length === 2) score += 0.5;
    if (right.length === 2) score += 0.5;
    if (left.length === 1) score -= 2;
    if (right.length === 1) score -= 2;
    if (!best || score > best.score) best = { left, right, score };
  }
  if (best && best.score >= 8) return [best.left, best.right];
  return null;
}

/**
 * Normalize employee → weekly-off policy mappings from attendance-structure API.
 */
function normalizeWorkspaceWoMappings(data = {}) {
  const result = {};
  Object.entries(data || {}).forEach(([empId, mapping]) => {
    if (Array.isArray(mapping)) {
      result[empId] = mapping.map((v) => Number(v)).filter((v) => !Number.isNaN(v));
      return;
    }
    if (mapping && typeof mapping === "object") {
      if (mapping.policyId && !mapping.policies) {
        result[empId] = {
          ...mapping,
          policies: [Number(mapping.policyId)].filter((v) => !Number.isNaN(v)),
        };
        return;
      }
      if (Array.isArray(mapping.policies)) {
        result[empId] = {
          ...mapping,
          policies: mapping.policies
            .map((v) => Number(v))
            .filter((v) => !Number.isNaN(v)),
        };
        return;
      }
    }
    result[empId] = mapping;
  });
  return result;
}

function isWorkspacePolicyEffectiveOnDate(policy, date) {
  if (!policy || !(date instanceof Date) || Number.isNaN(date.getTime())) return false;
  const start = policy.effectiveFrom || policy.effectiveDate || policy.startDate;
  const end = policy.effectiveTo || policy.endDate;
  const n =
    date.getFullYear() * 10000 + (date.getMonth() + 1) * 100 + date.getDate();
  if (start) {
    const s = String(start).slice(0, 10).replace(/-/g, "");
    if (s && n < Number(s)) return false;
  }
  if (end) {
    const e = String(end).slice(0, 10).replace(/-/g, "");
    if (e && n > Number(e)) return false;
  }
  return true;
}

/**
 * @returns {"WO"|"PH"|null}
 */
function resolvePolicyCalendarOffStatus(
  date,
  employee,
  weeklyOffPolicies = [],
  employeeWeeklyOffMappings = {},
  holidaysData = []
) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  const dateStr = `${y}-${m}-${d}`;
  const holHit = (holidaysData || []).some((h) => {
    const hd = String(h.date || h.holidayDate || "").slice(0, 10);
    return hd === dateStr && (h.isGlobal === true || h.isGlobal === 1 || h.isGlobal == null);
  });
  if (holHit) return "PH";

  const empId = employee?.id ?? employee?.employeeId;
  const dayName = date
    .toLocaleDateString("en-US", { weekday: "long" })
    .toLowerCase();
  let matchedPolicy = null;
  const mapping =
    empId != null
      ? employeeWeeklyOffMappings?.[String(empId)] ||
        employeeWeeklyOffMappings?.[empId]
      : null;
  if (mapping) {
    const policyIds = Array.isArray(mapping)
      ? mapping
      : mapping?.policies || [];
    matchedPolicy = (weeklyOffPolicies || []).find((p) =>
      policyIds.map(Number).includes(Number(p.id))
    );
  }
  if (!matchedPolicy) {
    matchedPolicy = (weeklyOffPolicies || []).find(
      (p) => isWorkspacePolicyEffectiveOnDate(p, date) && p.weeklyOffs
    );
  }
  if (matchedPolicy) {
    const cfg = matchedPolicy.weeklyOffs?.[dayName];
    if (cfg && cfg.enabled) return "WO";
    // Also honour weeklyOffConfigs array shape from some API payloads
    const configs = matchedPolicy.weeklyOffConfigs || matchedPolicy.WeeklyOffConfigs;
    if (Array.isArray(configs)) {
      const hit = configs.find(
        (c) =>
          String(c.day || "").toLowerCase() === dayName &&
          (c.enabled === true || c.enabled === 1 || c.enabled === "true")
      );
      if (hit) return "WO";
    }
  }
  // Fallback: Sat/Sun when policy could not be resolved — still warn on LP≠WO
  // so Import / Export confirm is not skipped silently.
  const dow = date.getDay(); // 0=Sun … 6=Sat
  if (dow === 0 || dow === 6) return "WO";
  return null;
}

/**
 * Validate attendance workbook: employee codes + day status codes (mirrors import rules).
 */
export async function validateWorkspaceAttImport(ctx) {
  if (!ctx?.file) throw new Error("Please choose a file");
  const ExcelJS = (await import("exceljs")).default;
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(await ctx.file.arrayBuffer());
  const ws =
    wb.worksheets.find((s) => !/instruction/i.test(s.name)) || wb.worksheets[0];
  if (!ws) throw new Error("No worksheet found");

  const header = ws.getRow(1);
  let codeCol = null;
  let nameCol = null;
  const dayCols = [];
  header.eachCell({ includeEmpty: false }, (cell, col) => {
    const raw = excelJsCellText(cell);
    const h = normalizeAttHeaderKey(raw);
    if (
      h === "employeecode" ||
      h === "empcode" ||
      h === "employeeid" ||
      h === "empid"
    ) {
      codeCol = col;
    } else if (h === "code" && codeCol == null) {
      codeCol = col;
    }
    if (h === "employeename" || h === "name") nameCol = col;
    const dayNum = Number(String(raw || "").trim());
    if (
      Number.isInteger(dayNum) &&
      dayNum >= 1 &&
      dayNum <= 31 &&
      String(raw).trim() === String(dayNum)
    ) {
      dayCols.push(col);
    }
  });
  // Template columns: Sl No | EmployeeCode | Name | …
  if (codeCol == null) codeCol = 2;
  if (nameCol == null) nameCol = 3;

  const allowedCodes = new Set(CORE_ATT_IMPORT_CODES);
  let halfDayDivider = "";
  let halfDayPrefix = "";
  try {
    const cfgResp = await attendanceService.getConfigurations();
    const rows = Array.isArray(cfgResp?.data)
      ? cfgResp.data
      : Array.isArray(cfgResp)
        ? cfgResp
        : [];
    if (cfgResp?.halfDayDivider != null && String(cfgResp.halfDayDivider).trim()) {
      halfDayDivider = String(cfgResp.halfDayDivider).trim();
    }
    if (cfgResp?.halfDayPrefix != null && String(cfgResp.halfDayPrefix).trim()) {
      halfDayPrefix = String(cfgResp.halfDayPrefix).trim();
    }
    rows.forEach((r) => {
      [
        r.importCode,
        r.import_code,
        r.exportCode,
        r.export_code,
        r.customCode,
        r.custom_code,
        r.originalCode,
        r.original_code,
        r.meaning,
      ].forEach((c) => {
        const u = String(c || "")
          .toUpperCase()
          .trim();
        if (u) allowedCodes.add(u);
      });
    });
  } catch {
    /* allow core codes only */
  }
  try {
    const ltResp = await attendanceStructureService.getLeaveTypes();
    const list = Array.isArray(ltResp?.data)
      ? ltResp.data
      : Array.isArray(ltResp)
        ? ltResp
        : [];
    list.forEach((lt) => {
      if (lt?.isActive === false) return;
      const u = String(lt?.code || "")
        .toUpperCase()
        .trim();
      if (u) allowedCodes.add(u);
    });
  } catch {
    /* ignore */
  }

  const statusAllowed = (raw) => {
    const up = String(raw || "").trim();
    if (!up || up === "-") return true;
    const upCode = up.toUpperCase();
    if (allowedCodes.has(upCode)) return true;

    const bothAllowed = (a, b) => {
      const x = String(a || "").toUpperCase().trim();
      const y = String(b || "").toUpperCase().trim();
      return x && y && allowedCodes.has(x) && allowedCodes.has(y);
    };

    const parts = isHalfDayIoFormatConfigured(halfDayDivider, halfDayPrefix)
      ? parseHalfDayIoStrict(up, halfDayDivider, halfDayPrefix)
      : parseHalfDayIoLenient(up, halfDayDivider, halfDayPrefix, (r) =>
          splitCombinedHalfDayCode(String(r || "").toUpperCase(), allowedCodes)
        );
    if (parts && parts.length === 2 && bothAllowed(parts[0], parts[1])) {
      return true;
    }
    return false;
  };

  const validRecords = [];
  const invalidRecords = [];
  const calendarOffConflicts = [];

  // Weekly Off / Holiday structure for WO-override warning (not a hard validation fail).
  let weeklyOffPolicies = [];
  let employeeWeeklyOffMappings = {};
  let holidaysData = [];
  try {
    const [woResp, mapResp, holResp] = await Promise.all([
      attendanceStructureService.getWeeklyOffPolicies(),
      attendanceStructureService.getEmployeeWeeklyOffPolicies(),
      attendanceStructureService.getHolidays(),
    ]);
    const rawPolicies = Array.isArray(woResp?.data)
      ? woResp.data
      : Array.isArray(woResp)
        ? woResp
        : [];
    weeklyOffPolicies = rawPolicies.map((p) => ({
      ...p,
      id: typeof p.id === "string" ? Number(p.id) : p.id,
    }));
    const rawMap =
      mapResp?.data && typeof mapResp.data === "object" && !Array.isArray(mapResp.data)
        ? mapResp.data
        : mapResp && typeof mapResp === "object" && !Array.isArray(mapResp)
          ? mapResp
          : {};
    employeeWeeklyOffMappings = normalizeWorkspaceWoMappings(rawMap);
    holidaysData = Array.isArray(holResp?.data)
      ? holResp.data
      : Array.isArray(holResp)
        ? holResp
        : [];
  } catch (structErr) {
    console.warn(
      "validateWorkspaceAttImport: WO/holiday structure load failed",
      structErr?.message || structErr
    );
  }

  // Employment window map (joining → relieved) from employee master.
  const empByCode = new Map();
  try {
    const pageLimit = 500;
    let page = 1;
    let maxPages = 1;
    const allEmps = [];
    while (page <= maxPages && page <= 40) {
      const empResp = await employeeService.getEmployees({
        page,
        limit: pageLimit,
        excludeEmploymentTypes: "External,external",
      });
      const pageRows =
        (Array.isArray(empResp?.employees) && empResp.employees) ||
        (Array.isArray(empResp?.data?.employees) && empResp.data.employees) ||
        (Array.isArray(empResp?.data?.rows) && empResp.data.rows) ||
        (Array.isArray(empResp?.rows) && empResp.rows) ||
        (Array.isArray(empResp?.data) && empResp.data) ||
        [];
      allEmps.push(...pageRows);
      const totalCount =
        empResp?.totalCount ??
        empResp?.meta?.total ??
        empResp?.total ??
        empResp?.data?.totalCount ??
        empResp?.data?.total;
      const totalPages =
        empResp?.totalPages ??
        empResp?.meta?.totalPages ??
        empResp?.data?.totalPages;
      if (totalPages) maxPages = Number(totalPages) || 1;
      else if (totalCount)
        maxPages = Math.max(1, Math.ceil(Number(totalCount) / pageLimit));
      else if (pageRows.length < pageLimit) maxPages = page;
      else maxPages = page + 1;
      page += 1;
    }
    allEmps.forEach((emp) => {
      const code = String(emp.empCode || emp.employeeId || emp.employeeCode || "")
        .trim()
        .toLowerCase();
      if (code) empByCode.set(code, emp);
    });
  } catch {
    /* sheet Joining/Relieved columns still used below */
  }

  // Prefer Joining / Relieved columns from the template when present.
  let joinCol = null;
  let relievedCol = null;
  header.eachCell({ includeEmpty: false }, (cell, col) => {
    const h = normalizeAttHeaderKey(excelJsCellText(cell));
    if (
      joinCol == null &&
      (h === "joiningdate" || h === "dateofjoining" || h === "doj" || h === "joindate")
    ) {
      joinCol = col;
    }
    if (
      relievedCol == null &&
      (h === "relieveddate" ||
        h === "relievingdate" ||
        h === "dateofrelieved" ||
        h === "exitdate" ||
        h === "doe")
    ) {
      relievedCol = col;
    }
  });

  const headerDayByCol = new Map();
  dayCols.forEach((col) => {
    const raw = excelJsCellText(header.getCell(col));
    const dayNum = Number(String(raw || "").trim());
    if (Number.isInteger(dayNum)) headerDayByCol.set(col, dayNum);
  });

  ws.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return;
    const code = excelJsCellText(row.getCell(codeCol));
    const name = excelJsCellText(row.getCell(nameCol));
    let hasAny = false;
    row.eachCell({ includeEmpty: false }, () => {
      hasAny = true;
    });
    if (!hasAny && !code && !name) return;

    const badStatuses = [];
    const employmentIssues = [];
    const emp = code ? empByCode.get(String(code).trim().toLowerCase()) : null;
    const sheetJoin = joinCol != null ? excelJsCellText(row.getCell(joinCol)) : "";
    const sheetRelieved =
      relievedCol != null ? excelJsCellText(row.getCell(relievedCol)) : "";
    const joinIso =
      parseEmpDateIso(sheetJoin) ||
      (emp ? parseEmpDateIso(emp.joiningDate || emp.joinDate || emp.joining_date) : null);
    const relievedIso =
      parseEmpDateIso(sheetRelieved) ||
      (emp
        ? parseEmpDateIso(
            emp.relievedDate ||
              emp.relieved_date ||
              emp.exitDate ||
              emp.relievingDate ||
              emp.resignationDate ||
              emp.terminationDate
          )
        : null);
    const joinNum = ymdNum(joinIso);
    const relievedNum = ymdNum(relievedIso);

    dayCols.forEach((col) => {
      const st = excelJsCellText(row.getCell(col));
      if (!st || st === "-") return;
      if (!statusAllowed(st)) badStatuses.push(st);
      const dayNum = headerDayByCol.get(col);
      const dateIso = toAttDateIso(ctx.year, ctx.month, dayNum);
      if (!dateIso) return;
      const dayN = ymdNum(dateIso);
      if (joinNum != null && dayN < joinNum) {
        employmentIssues.push(`${dateIso} before joining (${joinIso})`);
      } else if (relievedNum != null && dayN > relievedNum) {
        employmentIssues.push(`${dateIso} after relieved (${relievedIso})`);
      }

      // Policy WO/PH vs Excel status (warning on import confirm — not a hard fail)
      if (dayNum != null && statusAllowed(st)) {
        const d = new Date(ctx.year, ctx.month, dayNum);
        const policyOff = resolvePolicyCalendarOffStatus(
          d,
          emp,
          weeklyOffPolicies,
          employeeWeeklyOffMappings,
          holidaysData
        );
        if (policyOff) {
          const importHead = String(st || "")
            .toUpperCase()
            .trim()
            .split("/")[0];
          // LP / LOP / PR / etc. on WO/PH day → confirm before replace
          const offHeads = new Set(["WO", "PH", "HO"]);
          if (
            importHead &&
            importHead !== "-" &&
            !offHeads.has(importHead)
          ) {
            calendarOffConflicts.push({
              row: rowNumber,
              employeeCode: String(code).trim(),
              employeeName: name || "",
              date: dateIso,
              day: dayNum,
              policyStatus: policyOff,
              importStatus: String(st).trim().toUpperCase(),
            });
          }
        }
      }
    });

    const record = {
      "Employee Code": code,
      EmployeeCode: code,
      "Employee Name": name,
      Name: name || code,
    };
    if (!code) {
      invalidRecords.push({
        row: rowNumber,
        record,
        fieldErrors: { "Employee Code": "Employee Code is required" },
        error: "Employee Code is required",
      });
    } else if (employmentIssues.length) {
      const sample = [...new Set(employmentIssues)].slice(0, 4).join("; ");
      invalidRecords.push({
        row: rowNumber,
        record,
        fieldErrors: {
          Employment:
            `Attendance outside employment window: ${sample}` +
            (employmentIssues.length > 4
              ? ` (+${employmentIssues.length - 4} more)`
              : ""),
        },
        error: `Outside joining/relieved dates: ${sample}`,
      });
    } else if (badStatuses.length) {
      const sample = [...new Set(badStatuses)].slice(0, 5).join(", ");
      invalidRecords.push({
        row: rowNumber,
        record,
        fieldErrors: {
          Status: `Invalid status code(s): ${sample}. Use attendance import/export codes or active leave type codes.`,
        },
        error: `Invalid status code(s): ${sample}`,
      });
    } else {
      validRecords.push({ row: rowNumber, __row: rowNumber, record });
    }
  });

  return {
    validRecords,
    invalidRecords,
    validCount: validRecords.length,
    invalidCount: invalidRecords.length,
    totalRecords: validRecords.length + invalidRecords.length,
    hardBlockImport: false,
    calendarOffConflicts,
    file: ctx.file,
    year: ctx.year,
    month: ctx.month,
  };
}

export async function confirmWorkspaceAttImport(ctx, opts = {}) {
  if (!ctx?.file) throw new Error("Please choose a file");
  let fileToUpload = ctx.file;
  const successRows = Array.isArray(opts.successRows) ? opts.successRows : null;
  const ignoreFailed = !!opts.ignoreFailed;
  const overrideCalendarOff = opts.overrideCalendarOff === true;
  const conflictsToClear = Array.isArray(opts.clearCalendarOffConflicts)
    ? opts.clearCalendarOffConflicts
    : [];

  if (ignoreFailed && successRows && successRows.length) {
    const ExcelJS = (await import("exceljs")).default;
    const wb = new ExcelJS.Workbook();
    await wb.xlsx.load(await ctx.file.arrayBuffer());
    const ws =
      wb.worksheets.find((s) => !/instruction/i.test(s.name)) ||
      wb.worksheets[0];
    if (ws) {
      const keep = new Set(successRows.map((r) => Number(r.row)));
      for (let r = ws.rowCount; r >= 2; r -= 1) {
        if (!keep.has(r)) ws.spliceRows(r, 1);
      }
      const buf = await wb.xlsx.writeBuffer();
      fileToUpload = new File(
        [
          new Blob([buf], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          }),
        ],
        ctx.file.name || "Attendance_Import.xlsx",
        {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }
      );
    }
  }

  // No = keep policy WO/PH: blank conflicting day cells so they are not imported.
  if (!overrideCalendarOff && conflictsToClear.length) {
    const ExcelJS = (await import("exceljs")).default;
    const wb = new ExcelJS.Workbook();
    await wb.xlsx.load(await fileToUpload.arrayBuffer());
    const ws =
      wb.worksheets.find((s) => !/instruction/i.test(s.name)) ||
      wb.worksheets[0];
    if (ws) {
      const header = ws.getRow(1);
      const dayColByNum = new Map();
      header.eachCell({ includeEmpty: false }, (cell, col) => {
        const raw = excelJsCellText(cell);
        const dayNum = Number(String(raw || "").trim());
        if (
          Number.isInteger(dayNum) &&
          dayNum >= 1 &&
          dayNum <= 31 &&
          String(raw).trim() === String(dayNum)
        ) {
          dayColByNum.set(dayNum, col);
        }
      });
      const clearByCodeDay = new Set(
        conflictsToClear.map(
          (c) =>
            `${String(c.employeeCode || "").trim().toLowerCase()}|${Number(c.day)}`
        )
      );
      let codeCol = 2;
      header.eachCell({ includeEmpty: false }, (cell, col) => {
        const h = normalizeAttHeaderKey(excelJsCellText(cell));
        if (
          h === "employeecode" ||
          h === "empcode" ||
          h === "employeeid" ||
          h === "empid"
        ) {
          codeCol = col;
        }
      });
      ws.eachRow((row, rowNumber) => {
        if (rowNumber === 1) return;
        const code = String(excelJsCellText(row.getCell(codeCol)) || "")
          .trim()
          .toLowerCase();
        dayColByNum.forEach((col, dayNum) => {
          if (clearByCodeDay.has(`${code}|${dayNum}`)) {
            row.getCell(col).value = "";
          }
        });
      });      const buf = await wb.xlsx.writeBuffer();
      fileToUpload = new File(
        [
          new Blob([buf], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          }),
        ],
        ctx.file.name || "Attendance_Import.xlsx",
        {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }
      );
    }
  }

  const formData = new FormData();
  formData.append("file", fileToUpload);
  formData.append("year", String(ctx.year));
  formData.append("month", String(Number(ctx.month) + 1));
  if (overrideCalendarOff) {
    formData.append("overrideCalendarOff", "true");
  }
  const importResp = await attendanceService.importAttendance(formData);
  if (importResp && importResp.success === false) {
    throw new Error(importResp.message || "Import failed");
  }
  return importResp?.message || "Attendance import completed.";
}

export async function runWorkspaceIoAction(featureId, ctx) {
  if (featureId === "emp-import") {
    // Validate-only; UI shows ImportValidationReview then calls confirmWorkspaceEmpImport.
    return validateWorkspaceEmpImport(ctx);
  }

  if (featureId === "emp-export") {
    const section = ctx.section || "all";
    let res;
    let filename = "Employees_Export.xlsx";
    if (section === "all") {
      res = await employeeService.exportEmployees();
      filename = "All_Employees_Export.xlsx";
    } else {
      const fn = EMP_EXPORT_FN[section];
      if (!fn) throw new Error("Unknown export section");
      res = await fn();
      filename = `${EMP_TEMPLATE_LABELS[section] || section}_Export.xlsx`;
    }
    triggerBlobDownload(blobFromAxios(res), filename);
    return null;
  }

  if (featureId === "att-import") {
    if (!ctx.file) throw new Error("Please choose a file");
    const formData = new FormData();
    formData.append("file", ctx.file);
    formData.append("year", String(ctx.year));
    formData.append("month", String(Number(ctx.month) + 1));
    const importResp = await attendanceService.importAttendance(formData);
    if (importResp && importResp.success === false) {
      throw new Error(importResp.message || "Import failed");
    }
    return importResp?.message || "Attendance import completed.";
  }

  if (featureId === "att-import-leave") {
    if (!ctx.file) throw new Error("Please choose a file");
    // Leave apply logic lives in Attendance; keep the file ready for that screen.
    try {
      sessionStorage.setItem(
        "pacehrm:workspaceLeaveUpload",
        JSON.stringify({
          name: ctx.file.name,
          month: ctx.month,
          year: ctx.year,
          at: Date.now(),
        })
      );
    } catch {
      /* ignore */
    }
    return `File “${ctx.file.name}” is ready. Open Attendance → Bulk Leave Upload to apply leave rows for ${MONTH_LABEL(ctx.month)} ${ctx.year}.`;
  }

  if (featureId === "att-export") {
    const res = await attendanceService.exportAttendance(
      Number(ctx.month) + 1,
      ctx.year
    );
    triggerBlobDownload(
      blobFromAxios(res),
      `Attendance_Export_${ctx.year}_${String(ctx.month + 1).padStart(2, "0")}.xlsx`
    );
    return null;
  }

  if (featureId === "roles-export") {
    const params = {};
    if (Array.isArray(ctx.roleIds) && ctx.roleIds.length) {
      params.roleIds = ctx.roleIds.join(",");
    }
    const res = await api.get("/global-settings/roles-permissions/export", {
      params,
      responseType: "blob",
    });
    const suffix =
      Array.isArray(ctx.roleIds) && ctx.roleIds.length === 1
        ? `_${ctx.roleIds[0]}`
        : Array.isArray(ctx.roleIds) && ctx.roleIds.length
          ? `_${ctx.roleIds.length}roles`
          : "";
    triggerBlobDownload(blobFromAxios(res), `Roles_Permissions_Export${suffix}.xlsx`);
    return null;
  }

  if (featureId === "roles-import") {
    return validateWorkspaceRolesImport(ctx);
  }

  throw new Error("Unknown import/export action");
}

function MONTH_LABEL(monthIndex) {
  return [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ][monthIndex] || "";
}
